/**
 * Dice Controller - Manages dice throwing mechanic
 *
 * Implements the v2 pickup-throw system:
 * - Energy accumulation from swipe movement
 * - Multi-dice pickup
 * - Directional throw with accumulated power
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import gsap from 'gsap';
import { StateMachine } from '@sdl/shared';
import { DiceThrowState, DiceThrowContext, DicePhase, DiceThrowParams } from '@sdl/shared';
import { Engine } from '../../engine/Engine';
import { EventEmitter, DiceEvents } from '../../engine/core/EventEmitter';
import { sharedRaycaster, sharedPointer, tempPlane, tempVec3A } from '../../engine/core/SharedResources';
import { Dice } from './Dice';
import { preRunThrow } from './ThrowPreRun';
import { sound, SoundId } from '../../engine/audio/sound';
import { loadDefaultShootingConfig } from './ShootingConfig';
import type { DiceAssetManager } from './assets/DiceAssetManager';
import { isMobilePlatform } from '../../util/platform';

export interface DiceControllerConfig {
  diceCount?: number;
  energyGain?: number;      // Energy gain per pixel of movement
  maxEnergy?: number;
  minEnergyToThrow?: number;
  maxPower?: number;
  baseForward?: number;     // Forward force multiplier
  holdHeight?: number;      // Height when holding dice
  pickupRadius?: number;    // Radius to pick up dice
  lobStrength?: number;     // Upward lift force (0 = none, 1 = strong gust)
  lobDuration?: number;     // How long the lift lasts in seconds (0.1 - 0.5)
  diceMass?: number;        // Dice weight (0.5 = light, 1 = normal, 3+ = heavy)
  /**
   * Multiplier applied to `energyGain` on Android / iOS only. A screen
   * pixel on a phone is physically ~2-3× smaller than on a desktop
   * monitor, so the same finger travel registers less throw energy
   * than the equivalent mouse swipe. Touch-laptops are excluded —
   * they use a mouse-like grip and don't need the boost.
   */
  touchEnergyMultiplier?: number;
}

const DEFAULT_CONFIG: Required<DiceControllerConfig> = {
  diceCount: 3,
  energyGain: 0.3,
  maxEnergy: 100,
  minEnergyToThrow: 5,
  maxPower: 60,
  baseForward: 5,
  holdHeight: 4,
  pickupRadius: 1.5,
  lobStrength: 1.0,
  lobDuration: 0.2,
  diceMass: 1.0,
  touchEnergyMultiplier: 1.15,
};

// Minimum pointer movement (pixels) before accumulating throw energy (holding state uses HOLD_DEADZONE_PX)
// Note: Desktop mouse events have smaller deltas (1-3px) than mobile touch, so keep threshold low
const HOLD_DEADZONE_PX = 2;
// Gesture spin constants (currently unused; keep for future iteration)
// const CURVE_ARC_THRESHOLD = 0.6;     // radians of accumulated arc needed to trigger spin
// const CURVE_PATH_THRESHOLD = 24;     // pixels of path length to consider curve meaningful
// const SPIN_SCALE = 24;               // scales arc magnitude into spin
// const SPIN_MAX = 28;                 // clamp spin to avoid excessive angular velocity
// const SPIN_MIN = 4;                  // minimum spin when a curve is detected
const MIN_THROW_PATH = 18;           // pixels of path before a release can throw
// ── Throw pre-run (stuck-dice prevention) — see ThrowPreRun.ts ──────────────
// A throw must fully settle within this many simulated frames (4.5s @ 60fps,
// Phone Dice uses 300) or it's re-randomized. Generous: clean rolls sleep in
// ~120-180 frames; only wall-wedges and perpetual sliders blow the budget.
const PRERUN_FRAME_BUDGET = 270;
// Evaluations per throw before giving up (each costs one headless sim).
const PRERUN_MAX_ATTEMPTS = 5;
// Wall-clock guard: stop regenerating past this so a slow device's throw
// release never hitches noticeably.
const PRERUN_TIME_BUDGET_MS = 120;
// Shake rattle (HOLDING): a completed horizontal stroke must travel this far
// for its reversal to count as a shake beat — filters jitter and slow drift.
// Mobile needs both knobs higher: wrist shakes reverse direction much faster
// than mouse shakes and finger micro-jitter fakes reversals, so phone beats
// pinned to the desktop cooldown read as a machine gun (POCO F3 repro).
const SHAKE_STROKE_MIN_PX = 14;
const SHAKE_STROKE_MIN_PX_MOBILE = 26;
// Min gap between rattle plays. Pool 4 + pitch jitter on DICE_PICKUP make
// overlapping beats read as a natural dice rattle, not a machine gun.
const SHAKE_SOUND_COOLDOWN_MS = 140;
const SHAKE_SOUND_COOLDOWN_MS_MOBILE = 210;
// Shake-beat intensity: stroke speed (px/ms over the completed stroke) maps
// onto rattle volume. A lazy waggle plays at SHAKE_VOLUME_MIN of the catalog
// volume, a vigorous flick at full. Same scale feeds the relay so opponents
// hear how hard you're shaking.
const SHAKE_SPEED_SOFT = 0.25;  // px/ms — at/below this → minimum volume
const SHAKE_SPEED_HARD = 1.4;   // px/ms — at/above this → full volume
const SHAKE_VOLUME_MIN = 0.45;  // volume multiplier floor at soft speed

export class DiceController extends EventEmitter<DiceEvents> {
  private engine: Engine;
  private config: Required<DiceControllerConfig>;
  private dice: Dice[] = [];
  private stateMachine: StateMachine<DiceThrowContext, DiceThrowState>;
  private enabled = true;

  // Tracking
  private lastInputPosition = { x: 0, y: 0 };
  private accumulatedEnergy = 0;
  // Shake-rattle detection while HOLDING: a horizontal direction reversal
  // after a long-enough stroke is one "shake beat" → pickup sample plays as
  // a hand rattle. Scalar state only — no allocations, no per-frame work.
  private shakeDir = 0;
  private shakeStrokePx = 0;
  private shakeStrokeStartMs = 0;
  private lastShakeSoundTime = 0;
  private collectedDice: Set<Dice> = new Set();
  private lastEnergyUpdateTime = 0;
  private gesturePoints: Array<{ x: number; y: number }> = [];
  private gesturePathLength = 0;
  private gestureArcAngle = 0;

  // Hold animation
  private holdTime = 0;

  // Finger tracking for dice following
  private holdPosition = new THREE.Vector3();
  private isHolding = false;
  private readonly MAGNET_SPEED = 25;

  // Track if finger is currently touching (even during rolling/settling)
  private isTouching = false;
  private touchPosition = { x: 0, y: 0 };

  // Network sync
  private dragSyncInterval: ReturnType<typeof setInterval> | null = null;
  private readonly DRAG_SYNC_INTERVAL = 100; // Send position every 100ms for smooth sync
  private dragSeq = 0;

  // Server-authoritative state
  private isAuthorized = false;           // Server authorized us to roll
  private serverDicePhase: DicePhase = DicePhase.IDLE;
  private isServerAuthoritativeMode = false; // When true, wait for server events

  // HI_LO determinism: defer physics until server echo
  // When true, onThrowingEnter captures state and sends to server but keeps dice frozen.
  // Physics only starts when applyServerThrow() arrives.
  private deferPhysicsOnThrow = false;

  // Pickup label sprites (float above dice when waiting to be picked up)
  private pickupLabels: THREE.Sprite[] = [];
  private static pickupTexture: THREE.Texture | null = null;
  private static pickupTextureLoading = false;
  private pickupLabelsVisible = false;
  /** Pop-in scale multiplier animated by GSAP (0→1) */
  private pickupLabelPopScale = 0;

  // Dice asset manager (custom GLB dice skins)
  private assetManager: DiceAssetManager | null = null;
  private equippedDice: (string | null)[] = [];

  // Lob effect (upward wind gust during throw)
  private lobActive = false;
  private lobElapsed = 0;  // Seconds since lob started
  // @ts-expect-error - lobPower reserved for future lob intensity scaling
  private lobPower = 0;    // Cached power at throw time

  constructor(engine: Engine, config: DiceControllerConfig = {}) {
    super();

    this.engine = engine;

    // Load shooting config from localStorage (editor presets) and merge with defaults
    // Priority: passed config > localStorage preset > hardcoded defaults
    const savedShootingConfig = loadDefaultShootingConfig();
    this.config = { ...DEFAULT_CONFIG, ...savedShootingConfig, ...config };

    // Initialize state machine
    this.stateMachine = new StateMachine<DiceThrowContext, DiceThrowState>({
      id: 'dice-throw',
      initial: DiceThrowState.READY,
      context: {
        diceCount: this.config.diceCount,
        diceCollected: 0,
        energy: 0,
        maxEnergy: this.config.maxEnergy,
        minEnergyToThrow: this.config.minEnergyToThrow,
        swipeDirection: { x: 0, y: 0 },
        swipeVelocity: 0,
        dicePositions: [],
        diceRotations: [],
        diceResults: [],
        rollStartTime: 0,
        settleStartTime: 0,
      },
      states: [
        {
          id: DiceThrowState.READY,
          onEnter: () => this.onReadyEnter(),
          transitions: [
            { to: DiceThrowState.PICKING_UP },
          ],
        },
        {
          id: DiceThrowState.PICKING_UP,
          onEnter: () => this.onPickingUpEnter(),
          onUpdate: (_ctx, dt) => this.onPickingUpUpdate(dt),
          transitions: [
            { to: DiceThrowState.HOLDING, guard: () => this.allDiceCollected() },
            { to: DiceThrowState.READY },
          ],
        },
        {
          id: DiceThrowState.HOLDING,
          onEnter: () => this.onHoldingEnter(),
          onUpdate: (_ctx, dt) => this.onHoldingUpdate(dt),
          transitions: [
            { to: DiceThrowState.THROWING },
            { to: DiceThrowState.READY },
          ],
        },
        {
          id: DiceThrowState.THROWING,
          onEnter: () => this.onThrowingEnter(),
          transitions: [
            { to: DiceThrowState.ROLLING },
          ],
        },
        {
          id: DiceThrowState.ROLLING,
          onEnter: () => this.onRollingEnter(),
          onUpdate: (_ctx, dt) => this.onRollingUpdate(dt),
          transitions: [
            { to: DiceThrowState.SETTLING, guard: () => this.areDiceSlowing() },
          ],
        },
        {
          id: DiceThrowState.SETTLING,
          onEnter: () => this.onSettlingEnter(),
          onUpdate: (_ctx, dt) => this.onSettlingUpdate(dt),
          transitions: [
            { to: DiceThrowState.STOPPED, guard: () => this.areDiceStopped() },
          ],
        },
        {
          id: DiceThrowState.STOPPED,
          onEnter: () => this.onStoppedEnter(),
          transitions: [
            { to: DiceThrowState.READY },
          ],
        },
      ],
    });

    // Setup input handlers
    this.setupInputHandlers();

    // Preload pickup label texture
    this.ensurePickupTexture();
  }

  private setupInputHandlers(): void {
    // Touch/pointer start
    this.engine.on('input:touch_start', (data) => this.onInputStart(data.x, data.y));
    this.engine.on('input:pointer_down', (data) => this.onInputStart(data.x, data.y));

    // Touch/pointer move
    this.engine.on('input:touch_move', (data) => this.onInputMove(data.x, data.y));
    this.engine.on('input:pointer_move', (data) => this.onInputMove(data.x, data.y));

    // Touch/pointer end
    this.engine.on('input:touch_end', (data) => this.onInputEnd(data.x, data.y));
    this.engine.on('input:pointer_up', (data) => this.onInputEnd(data.x, data.y));
  }

  /** Set the dice asset manager for loading custom dice skins */
  setAssetManager(manager: DiceAssetManager): void {
    this.assetManager = manager;
  }

  /** Set the equipped dice IDs (from active preset slots) */
  setEquippedDice(diceIds: (string | null)[]): void {
    this.equippedDice = diceIds;
  }

  /** Get the equipped dice IDs */
  getEquippedDice(): (string | null)[] {
    return this.equippedDice;
  }

  /**
   * Initialize dice
   */
  createDice(positions: Array<{ x: number; y: number; z: number }>): void {
    // Clear existing dice (release assets back to pool)
    this.releaseDiceAssets();
    this.dice = [];

    // Create new dice — use custom GLB skins if available, otherwise procedural fallback
    for (let i = 0; i < positions.length; i++) {
      const diceId = this.equippedDice[i] ?? null;
      let asset = null;

      if (diceId && this.assetManager?.isReady(diceId)) {
        asset = this.assetManager.acquireDice(diceId);
      }

      const die = new Dice(this.engine.physics, this.engine.scene, {
        position: positions[i],
        size: 1,
        color: 0xcc0000,
      }, asset ?? undefined);

      this.dice.push(die);
    }
  }

  clearDice(): void {
    if (this.dice.length > 0) {
      console.warn('[DiceController] clearDice REMOVING local dice', {
        diceCount: this.dice.length,
        stack: new Error().stack?.split('\n').slice(1, 6).join(' | '),
      });
    }

    this.disposePickupLabels();
    this.releaseDiceAssets();
    this.dice = [];
    this.collectedDice.clear();
    this.accumulatedEnergy = 0;
    this.isHolding = false;
    this.stateMachine.forceTransitionTo(DiceThrowState.READY);
  }

  // ── Pickup Labels ──

  /** Load the pickup texture (shared across all controllers) */
  private ensurePickupTexture(): void {
    if (DiceController.pickupTexture || DiceController.pickupTextureLoading) return;
    DiceController.pickupTextureLoading = true;
    new THREE.TextureLoader().load('/assets/icons/pickup-dice.png', (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      DiceController.pickupTexture = tex;
      DiceController.pickupTextureLoading = false;
    });
  }

  /** Create a pickup label sprite */
  private createPickupLabel(): THREE.Sprite {
    const mat = new THREE.SpriteMaterial({
      map: DiceController.pickupTexture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(mat);
    // Size: roughly 1.2 wide, aspect from the image (~370x310 → ~1.2 aspect)
    const aspect = 370 / 310;
    const height = 1.0;
    sprite.scale.set(height * aspect, height, 1);
    sprite.visible = false;
    return sprite;
  }

  /** Show pickup labels above all uncollected dice */
  showPickupLabels(): void {
    if (!DiceController.pickupTexture) {
      this.ensurePickupTexture();
      return; // Will show on next call once texture loads
    }
    // Ensure we have one label per die
    while (this.pickupLabels.length < this.dice.length) {
      const label = this.createPickupLabel();
      this.engine.scene.add(label);
      this.pickupLabels.push(label);
    }
    // Show labels above uncollected dice, hide for collected
    for (let i = 0; i < this.dice.length; i++) {
      const die = this.dice[i];
      const label = this.pickupLabels[i];
      const isCollected = this.collectedDice.has(die);
      label.visible = !isCollected;
      if (!isCollected) {
        const pos = die.mesh.position;
        label.position.set(pos.x, pos.y + 1.8, pos.z);
      }
    }
    this.pickupLabelsVisible = true;

    // Pop-in animation
    this.pickupLabelPopScale = 0;
    gsap.to(this, { pickupLabelPopScale: 1, duration: 0.35, ease: 'back.out(1.7)' });
  }

  /** Hide all pickup labels */
  hidePickupLabels(): void {
    for (const label of this.pickupLabels) {
      label.visible = false;
    }
    this.pickupLabelsVisible = false;
  }

  /** Remove and dispose all pickup labels */
  private disposePickupLabels(): void {
    for (const label of this.pickupLabels) {
      label.removeFromParent();
      (label.material as THREE.SpriteMaterial).dispose();
    }
    this.pickupLabels = [];
    this.pickupLabelsVisible = false;
  }

  /**
   * Single authority for pickup label visibility and positioning (runs every frame).
   * Labels show when: enabled + dice exist + authorized (or non-authoritative) + state allows pickup.
   */
  private updatePickupLabels(): void {
    const state = this.stateMachine.current;
    const canPickup = this.enabled
      && this.dice.length > 0
      && (!this.isServerAuthoritativeMode || this.isAuthorized)
      && (state === DiceThrowState.READY
        || state === DiceThrowState.STOPPED
        || state === DiceThrowState.PICKING_UP);

    if (canPickup) {
      // Ensure labels exist
      if (!this.pickupLabelsVisible) {
        this.showPickupLabels();
      }
      // Update positions, visibility, and constant screen-size scaling
      const camera = this.engine.camera;
      const vFov = camera.fov * (Math.PI / 180);
      // Target height in screen fraction (~0.06 = 6% of screen height)
      const TARGET_SCREEN_HEIGHT = 0.075;
      const baseHeight = 2 * Math.tan(vFov / 2) * TARGET_SCREEN_HEIGHT;
      const aspect = 370 / 310;

      for (let i = 0; i < this.dice.length && i < this.pickupLabels.length; i++) {
        const die = this.dice[i];
        const label = this.pickupLabels[i];
        if (this.collectedDice.has(die)) {
          label.visible = false;
        } else {
          label.visible = true;
          const pos = die.mesh.position;
          label.position.set(pos.x, pos.y + 1.8, pos.z);
          // Scale to maintain constant screen size, with pop-in animation
          const dist = camera.position.distanceTo(label.position);
          const h = baseHeight * dist * this.pickupLabelPopScale;
          label.scale.set(h * aspect, h, 1);
        }
      }
    } else if (this.pickupLabelsVisible) {
      this.hidePickupLabels();
    }
  }

  /**
   * Random face-aligned cube orientation (Rubik's-cube-style snap).
   * Picks uniformly from the 24 cube-aligned orientations: 6 face
   * choices × 4 yaw turns. Keeps the cube oriented to world axes so
   * the throw contacts floor and walls on flat faces, not on edges
   * or corners — feels organic.
   *
   * Rejected alternative (Shoemake's uniform random quaternion): gave
   * arbitrarily tilted starting poses, so dice hit walls at weird
   * angles and bounced off-axis. Looked fine in still frames; felt
   * unnatural in motion.
   */
  private randomFaceAlignedQuaternion(): { x: number; y: number; z: number; w: number } {
    const faceIdx = Math.floor(Math.random() * 6);
    const yawTurns = Math.floor(Math.random() * 4);

    const face = new THREE.Quaternion();
    switch (faceIdx) {
      case 0: /* +Y up: identity */ break;
      case 1: face.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI); break;       // -Y up
      case 2: face.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2); break;   // +Z up
      case 3: face.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2); break;  // -Z up
      case 4: face.setFromAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2); break;  // +X up
      case 5: face.setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI / 2); break;   // -X up
    }

    const yaw = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      yawTurns * Math.PI / 2,
    );

    // yaw applied first (around local +Y), then face rotation tips
    // the cube. Result: yaw rotates around whichever face ends up on
    // top, giving 24 distinct cube-aligned orientations.
    const result = new THREE.Quaternion().multiplyQuaternions(face, yaw);
    return { x: result.x, y: result.y, z: result.z, w: result.w };
  }

  /** Release dice assets back to the pool, dispose procedural dice */
  private releaseDiceAssets(): void {
    for (const die of this.dice) {
      if (die.asset && this.assetManager) {
        // Remove from scene + physics, then return mesh to pool
        die.dispose();
        this.assetManager.releaseDice(die.asset);
      } else {
        die.dispose();
      }
    }
  }

  /**
   * Update (call every frame)
   */
  update(deltaTime: number): void {
    this.stateMachine.update(deltaTime);
    this.updatePickupLabels();

    // Update shader uniforms for animated dice skins (holographic, neon glow, etc.)
    if (this.assetManager) {
      this.assetManager.updateShaderTime(performance.now() / 1000);
    }

    // Apply lob force (upward "wind gust" during initial throw)
    if (this.lobActive) {
      this.lobElapsed += deltaTime;

      if (this.lobElapsed < this.config.lobDuration) {
        // Calculate force - stronger at start, fades over duration
        // Force is tuned to work WITH gravity (30), not overpower it
        // lobStrength 1.0 = roughly equal to gravity, so dice float briefly
        const progress = this.lobElapsed / this.config.lobDuration;
        const fadeOut = 1 - progress; // 1.0 at start, 0.0 at end

        // Apply upward force to all dice
        // Force scales with mass so the arc effect remains consistent
        // (otherwise heavy dice would barely arc since F=ma means a=F/m gets tiny)
        for (const die of this.dice) {
          if (!die.held) {
            // Scale lob force by mass so acceleration is consistent
            // This gives same arc trajectory regardless of mass
            const lobForce = this.config.lobStrength * 25 * die.body.mass * fadeOut;
            die.body.applyForce(
              new CANNON.Vec3(0, lobForce, 0),
              die.body.position
            );
          }
        }
      } else {
        // Lob duration complete
        this.lobActive = false;
      }
    }

    // Sync all dice visuals to physics (skip held dice - they're controlled directly)
    for (const die of this.dice) {
      if (!die.held) {
        die.syncToPhysics(deltaTime);
      }
    }
  }

  // ============================================================================
  // INPUT HANDLERS
  // ============================================================================

  private onInputStart(x: number, y: number): void {
    if (!this.enabled) return;

    // In server-authoritative mode, check authorization
    if (this.isServerAuthoritativeMode && !this.isAuthorized) {
      return;
    }

    this.lastInputPosition = { x, y };
    this.resetGestureTrail(x, y);

    // Always track touch state (even during rolling/settling)
    this.isTouching = true;
    this.touchPosition = { x, y };

    const state = this.stateMachine.current;

    // In STOPPED or READY state, player can pick up dice from where they are
    if (state === DiceThrowState.STOPPED || state === DiceThrowState.READY) {
      // If stopped, just transition to ready (no dice teleport!)
      if (state === DiceThrowState.STOPPED) {
        this.collectedDice.clear();
        this.accumulatedEnergy = 0;
        this.holdTime = 0;
        this.stateMachine.forceTransitionTo(DiceThrowState.READY);
      }

      // Emit pickup start event for server (in authoritative mode)
      if (this.isServerAuthoritativeMode) {
        this.emit('dice:pickup_start_request', { timestamp: Date.now() });
        this.serverDicePhase = DicePhase.COLLECTING;
      }

      // Start holding/tracking touch - allows drag-to-collect
      this.isHolding = true;
      this.holdPosition = this.screenToWorld(x, y, this.config.holdHeight);
      this.stateMachine.transitionTo(DiceThrowState.PICKING_UP);

      // Try to pick up a die if touching near one
      const groundPos = this.screenToWorld(x, y, 0.5);
      const die = this.findDiceNearPoint(groundPos);

      if (die) {
        this.pickupDie(die);
      }
    }
  }

  private onInputMove(x: number, y: number): void {
    if (!this.enabled) return;
    const dx = x - this.lastInputPosition.x;
    const dy = y - this.lastInputPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Always track touch position (even during rolling/settling)
    this.touchPosition = { x, y };

    const state = this.stateMachine.current;

    // Update hold position to follow finger
    if (this.isHolding) {
      this.holdPosition = this.screenToWorld(x, y, this.config.holdHeight);
    }

    if (state === DiceThrowState.PICKING_UP) {
      // Try to collect more dice as we drag over them
      const groundPos = this.screenToWorld(x, y, 0.5);
      const die = this.findDiceNearPoint(groundPos);
      if (die && !this.collectedDice.has(die)) {
        this.pickupDie(die);
      }

      // Check if all dice collected
      if (this.allDiceCollected()) {
        this.stateMachine.transitionTo(DiceThrowState.HOLDING);
      }
    } else if (state === DiceThrowState.HOLDING) {
      const now = performance.now();

      // Continue accumulating energy (ignore micro jitter with higher deadzone while holding)
      if (distance >= HOLD_DEADZONE_PX) {
        // Mobile boost — see DiceControllerConfig.touchEnergyMultiplier
        // for rationale. No-op on desktop / touch-laptops.
        const gain = isMobilePlatform()
          ? this.config.energyGain * this.config.touchEnergyMultiplier
          : this.config.energyGain;
        this.accumulatedEnergy += distance * gain;
        this.accumulatedEnergy = Math.min(this.accumulatedEnergy, this.config.maxEnergy);

        // Update swipe direction
        const ctx = this.stateMachine.getContext();
        ctx.swipeDirection = {
          x: dx / distance,
          y: dy / distance,
        };

        this.lastEnergyUpdateTime = now;
      } else {
        // Instantly clear energy when essentially stationary while holding
        if (this.accumulatedEnergy !== 0) {
          this.accumulatedEnergy = 0;
          this.lastEnergyUpdateTime = now;
        }
      }

      this.emit('dice:energy_change', {
        energy: this.accumulatedEnergy,
        maxEnergy: this.config.maxEnergy,
      });

      this.detectShake(dx, now);
      this.addGesturePoint(x, y);
    }

    this.lastInputPosition = { x, y };
  }

  /**
   * Shake-rattle: track the sign of horizontal movement; when the direction
   * reverses after a stroke of at least SHAKE_STROKE_MIN_PX, play one rattle
   * beat (cooldown-limited) and emit dice:shake for the network relay. A
   * single-direction throw windup never reverses, so it can't false-positive.
   */
  private detectShake(dx: number, now: number): void {
    const dir = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    if (dir === 0) return;
    if (dir === this.shakeDir) {
      this.shakeStrokePx += Math.abs(dx);
      return;
    }
    const prevStroke = this.shakeStrokePx;
    const strokeDurationMs = Math.max(1, now - this.shakeStrokeStartMs);
    this.shakeDir = dir;
    this.shakeStrokePx = Math.abs(dx);
    this.shakeStrokeStartMs = now;
    const mobile = isMobilePlatform();
    if (prevStroke < (mobile ? SHAKE_STROKE_MIN_PX_MOBILE : SHAKE_STROKE_MIN_PX)) return;
    if (now - this.lastShakeSoundTime < (mobile ? SHAKE_SOUND_COOLDOWN_MS_MOBILE : SHAKE_SOUND_COOLDOWN_MS)) return;
    this.lastShakeSoundTime = now;
    // Beat intensity from the completed stroke's speed: a short fast flick
    // is a hard shake, a long slow drag is a soft one.
    const speed = prevStroke / strokeDurationMs; // px/ms
    const intensity = Math.min(1, Math.max(0, (speed - SHAKE_SPEED_SOFT) / (SHAKE_SPEED_HARD - SHAKE_SPEED_SOFT)));
    sound.play(SoundId.DICE_PICKUP, { volume: SHAKE_VOLUME_MIN + (1 - SHAKE_VOLUME_MIN) * intensity });
    this.emit('dice:shake', { intensity });
  }

  private onInputEnd(_x: number, _y: number): void {
    if (!this.enabled) return;
    // Clear touch tracking
    this.isTouching = false;

    const state = this.stateMachine.current;

    if (state === DiceThrowState.HOLDING) {
      this.isHolding = false;
      const now = performance.now();
      const recentMs = now - this.lastEnergyUpdateTime;
      if (recentMs > 120 && this.accumulatedEnergy > 0) {
        this.accumulatedEnergy = 0;
        this.emit('dice:energy_change', {
          energy: this.accumulatedEnergy,
          maxEnergy: this.config.maxEnergy,
        });
      }

      const hasRecentEnergy = recentMs <= 200;
      const hasEnoughPath = this.gesturePathLength >= MIN_THROW_PATH;

      if (hasRecentEnergy && hasEnoughPath && this.accumulatedEnergy >= this.config.minEnergyToThrow) {
        this.stateMachine.transitionTo(DiceThrowState.THROWING);
      } else {
        // Not enough energy, drop dice
        this.releaseAllDice();
        this.stateMachine.transitionTo(DiceThrowState.READY);
      }
    } else if (state === DiceThrowState.PICKING_UP) {
      // Cancelled pickup
      this.isHolding = false;
      this.releaseAllDice();
      this.stateMachine.transitionTo(DiceThrowState.READY);
    }
  }

  // ============================================================================
  // STATE HANDLERS
  // ============================================================================

  private onReadyEnter(): void {
    this.accumulatedEnergy = 0;
    this.collectedDice.clear();
    this.isHolding = false;
    this.holdTime = 0;
    this.resetGestureTrail();

  }

  private onPickingUpEnter(): void {
    // Start drag sync interval for network
    this.startDragSync();

    // Reset energy/swing at pickup start so power only comes from deliberate post-collect swipe
    this.accumulatedEnergy = 0;
    this.lastEnergyUpdateTime = performance.now();
    const stoppedCtx = this.stateMachine.getContext();
    stoppedCtx.swipeDirection = { x: 0, y: 0 };
    this.emit('dice:energy_change', {
      energy: this.accumulatedEnergy,
      maxEnergy: this.config.maxEnergy,
    });

    this.resetGestureTrail();
  }

  private onPickingUpUpdate(deltaTime: number): void {
    this.updateHeldDicePositions(deltaTime);
  }

  private onHoldingEnter(): void {
    this.emit('dice:all_collected', undefined);

    // Reset energy so throw power only comes from deliberate post-collect swipe (prevents pickup path from forcing hard throws)
    this.accumulatedEnergy = 0;
    this.lastEnergyUpdateTime = performance.now();
    // Fresh shake state per hold — a leftover stroke from the previous hold
    // must not count toward this hold's first reversal.
    this.shakeDir = 0;
    this.shakeStrokePx = 0;
    this.shakeStrokeStartMs = performance.now();
    const stoppedCtx = this.stateMachine.getContext();
    stoppedCtx.swipeDirection = { x: 0, y: 0 };
    this.emit('dice:energy_change', {
      energy: this.accumulatedEnergy,
      maxEnergy: this.config.maxEnergy,
    });

    // Emit collected event for server (in authoritative mode)
    if (this.isServerAuthoritativeMode) {
      this.emit('dice:collected_request', { timestamp: Date.now() });
      this.serverDicePhase = DicePhase.HOLDING;
    }

    this.resetGestureTrail();
  }

  private onHoldingUpdate(deltaTime: number): void {
    this.holdTime += deltaTime;
    this.updateHeldDicePositions(deltaTime);
  }

  private onThrowingEnter(): void {
    // Stop drag sync - we're about to throw
    this.stopDragSync();

    const ctx = this.stateMachine.getContext();
    const power = (this.accumulatedEnergy / this.config.maxEnergy) * this.config.maxPower;

    // Calculate throw direction (forward only - lob is applied as force over time)
    const forwardDir = {
      x: ctx.swipeDirection.x * 0.3,
      z: -1 * this.config.baseForward,
    };

    // Normalize forward direction and scale by power
    const forwardLength = Math.sqrt(forwardDir.x ** 2 + forwardDir.z ** 2) || 1;
    const direction = {
      x: (forwardDir.x / forwardLength) * power,
      y: 0.1 * power,  // Tiny upward so dice don't scrape the ground
      z: (forwardDir.z / forwardLength) * power,
    };

    // Apply lob as a single upward impulse at throw time (deterministic).
    // Previously this was a per-frame force over lobDuration which caused
    // non-deterministic results due to variable frame timing.
    const lobImpulse = this.config.lobStrength * 5; // Tuned to match previous arc height
    for (const die of this.dice) {
      if (!die.held) {
        const impulseY = lobImpulse * die.body.mass;
        die.body.applyImpulse(
          new CANNON.Vec3(0, impulseY, 0),
          die.body.position
        );
      }
    }
    // Disable per-frame lob (kept for reference but not activated)
    this.lobActive = false;
    this.lobElapsed = 0;
    this.lobPower = power;

    // Throw spin: power-scaled magnitude, swipe-direction bias on Y
    // (yaw — feels like wrist motion), per-die random for X / Z so the
    // 3 dice tumble independently. Min baseline guarantees a visible
    // tumble even on a soft throw, so dice never go dead-straight.
    //
    // Determinism is preserved: each die's angularVelocity is captured
    // into diceStates below and broadcast via throw_request — other
    // clients apply the exact same value.
    const power01 = Math.min(1, Math.max(0, this.accumulatedEnergy / this.config.maxEnergy));
    const SPIN_MIN = 5;
    const SPIN_MAX = 16;
    const spinMag = SPIN_MIN + (SPIN_MAX - SPIN_MIN) * power01;
    const swipeYawBias = ctx.swipeDirection.x; // -1..1, left/right wrist

    // Throw all dice and capture their states immediately
    const diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }> = [];

    for (let i = 0; i < this.dice.length; i++) {
      const die = this.dice[i];

      // Snap to a random face-aligned orientation right before the
      // throw (Rubik's-cube-style: pick one of 24 cube orientations).
      // Without this, dice carry the pickup-time pose (= last stopped
      // pose) all the way through the hand → release, clustering
      // outcomes around whatever face was on top when grabbed. The
      // chosen quaternion is captured into getRotation() below and
      // broadcast via throw_request — determinism preserved across
      // clients.
      //
      // Why face-aligned, not arbitrary 3D random: a uniform-random
      // quaternion (Shoemake) gives weird tilted poses, so the cube
      // contacts walls / floor on edges or corners and bounces in
      // non-organic directions. Face-aligned starts keep the throw
      // physics natural while still randomizing the initial face.
      //
      // No angular velocity is applied — dice fly straight from the
      // hand, the random face × floor/wall contacts produce the final
      // outcome distribution.
      const randomQuat = this.randomFaceAlignedQuaternion();
      die.setRotationFromQuaternion(randomQuat);

      // Per-die spin axis: random X / Z components (independent tumble),
      // Y biased by swipe direction (shared wrist gesture). All clients
      // recompute the SAME random values? No — local computes, broadcast
      // via diceStates below.
      const spin = this.makeThrowSpin(spinMag, swipeYawBias);

      die.throw(
        direction,
        1,
        spin,
        this.config.diceMass,
      );

      // Capture state immediately after throw
      diceStates.push({
        position: die.getPosition(),
        rotation: die.getRotation(),
        velocity: die.getVelocity(),
        angularVelocity: die.getAngularVelocity(),
      });
    }

    // Stuck-throw prevention (Phone Dice's preRun): simulate the captured
    // throw ahead of time; if it wedges/leans/never settles, re-randomize
    // the orientation + tumble (the gesture's velocity is untouched) and
    // re-evaluate. Mutates diceStates in place to the ACCEPTED candidate,
    // which is what gets broadcast below — spectators replay the same
    // validated throw. Skipped for HI_LO (deferred physics + multi-player
    // dice interactions make a solo pre-run meaningless there).
    if (!this.deferPhysicsOnThrow) {
      this.preRunAndFixThrow(diceStates, spinMag, swipeYawBias);
    }

    // Update server phase tracking
    if (this.isServerAuthoritativeMode) {
      this.serverDicePhase = DicePhase.THROWN;
      this.emit('dice:throw_request', {
        diceStates,
        halfExtents: this.dice[0]?.physicsHalfExtents,
        timestamp: Date.now(),
      });
    }

    // HI_LO determinism: freeze dice after capturing state.
    // Physics will only start when applyServerThrow() arrives from server echo.
    // This ensures all dice (local + opponent) enter physics from server broadcasts.
    if (this.deferPhysicsOnThrow) {
      for (const die of this.dice) {
        die.pickup(); // mass=0, no collision — freezes die at throw position
      }
    }

    this.resetGestureTrail();

    // Emit throw sync event with full physics state for network (legacy)
    this.emit('dice:throw_sync', { diceStates });

    sound.play(SoundId.DICE_THROW);
    this.emit('dice:throw', {
      direction: { x: ctx.swipeDirection.x, y: ctx.swipeDirection.y },
      power,
    });

    // Transition to rolling (unless deferred — applyServerThrow will force ROLLING)
    if (!this.deferPhysicsOnThrow) {
      this.stateMachine.transitionTo(DiceThrowState.ROLLING);
    }
  }

  /**
   * Per-die throw spin: random X / Z tumble axes, Y biased by the swipe
   * (wrist yaw), normalized and scaled to spinMag. Shared by the initial
   * throw and pre-run regeneration so re-rolled candidates draw from the
   * same distribution as first throws.
   */
  private makeThrowSpin(spinMag: number, swipeYawBias: number): { x: number; y: number; z: number } {
    const axRandX = (Math.random() - 0.5) * 2;        // -1..1
    const axRandZ = (Math.random() - 0.5) * 2;
    const axY = swipeYawBias * 1.5 + (Math.random() - 0.5) * 0.5;
    const axLen = Math.sqrt(axRandX * axRandX + axY * axY + axRandZ * axRandZ) || 1;
    return {
      x: (axRandX / axLen) * spinMag,
      y: (axY / axLen) * spinMag,
      z: (axRandZ / axLen) * spinMag,
    };
  }

  /**
   * Evaluate the throw loaded into the dice bodies via preRunThrow; while it
   * fails (wedged / leaning / still moving at the frame budget), re-randomize
   * each die's orientation + tumble and re-evaluate. Velocity — the part the
   * player authored with their gesture — is never touched, so the throw feel
   * is identical; only the die randomness changes, which a first throw drew
   * from the same distributions anyway.
   *
   * Runs synchronously between fixed updates: the bodies still hold the
   * captured initial state, no physics step has consumed it yet, so editing
   * quaternion/angularVelocity here is invisible. `diceStates` is updated in
   * place to match the accepted candidate (it's broadcast right after).
   *
   * Gives up after MAX_ATTEMPTS evaluations or TIME_BUDGET_MS wall-clock
   * (slow devices) and keeps the last candidate — the live nudge/dampen
   * pipeline remains as the rescue path, exactly as before this existed.
   */
  private preRunAndFixThrow(
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>,
    spinMag: number,
    swipeYawBias: number,
  ): void {
    if (this.dice.length === 0) return;
    const world = this.engine.physics.world;
    const dt = this.engine.fixedDelta;
    const t0 = performance.now();

    // The simulated steps fire real 'collide' events on the dice — mute
    // globally for the duration (always restored via finally).
    sound.suspend(true);
    try {
      for (let attempt = 1; attempt <= PRERUN_MAX_ATTEMPTS; attempt++) {
        const verdict = preRunThrow(world, this.dice, PRERUN_FRAME_BUDGET, dt);
        if (verdict.ok) {
          if (attempt > 1) {
            console.log(`[ThrowPreRun] clean throw after ${attempt - 1} regen(s) — settles in ${verdict.frames} frames`);
          }
          return;
        }
        if (attempt === PRERUN_MAX_ATTEMPTS || performance.now() - t0 > PRERUN_TIME_BUDGET_MS) {
          console.warn(`[ThrowPreRun] giving up after ${attempt} attempt(s) (${verdict.reason}) — live nudge/dampen will rescue`);
          return;
        }

        // Reject — redraw the random components and try again.
        for (let i = 0; i < this.dice.length; i++) {
          const die = this.dice[i];
          die.setRotationFromQuaternion(this.randomFaceAlignedQuaternion());
          const spin = this.makeThrowSpin(spinMag, swipeYawBias);
          die.body.angularVelocity.set(spin.x, spin.y, spin.z);
          diceStates[i].rotation = die.getRotation();
          diceStates[i].angularVelocity = die.getAngularVelocity();
        }
      }
    } finally {
      sound.suspend(false);
      // CRITICAL determinism fix — re-apply the accepted throw vectors to the
      // live dice (mirrors Phone Dice's playerShoot re-applying vectors after
      // preRun). The pre-run STEPS the live dice, which SLEEP mid-sim; cannon
      // zeroes a sleeping body's inertia tensor, and preRunThrow's snapshot
      // restores pose/velocity but NOT that tensor. So after the pre-run the
      // dice body is subtly corrupted, and the shooter's real roll diverges
      // from every spectator (who simulate the clean broadcast vectors) — the
      // dice "land in one spot then snap to another" on the result. Resetting
      // the body from diceStates and calling release() rebuilds mass/inertia,
      // wakes the body, and re-arms the dice-vs-dice flight mute, so the
      // shooter rolls bit-identically to what it broadcast. Verified 0.0
      // shooter-vs-spectator divergence (was ~2.5 units). diceStates holds the
      // accepted candidate whether the first attempt passed or a later one did.
      for (let i = 0; i < this.dice.length && i < diceStates.length; i++) {
        const die = this.dice[i];
        const s = diceStates[i];
        die.body.velocity.setZero();
        die.body.angularVelocity.setZero();
        die.body.force.setZero();
        die.body.torque.setZero();
        die.setPosition(s.position);
        die.setRotationFromQuaternion(s.rotation);
        die.setVelocity(s.velocity);
        die.setAngularVelocity(s.angularVelocity);
        die.release();
      }
    }
  }

  private onRollingEnter(): void {
    const ctx = this.stateMachine.getContext();
    ctx.rollStartTime = performance.now();
    this.emit('dice:rolling', undefined);
  }

  private onRollingUpdate(_deltaTime: number): void {
    // Check if dice are slowing down
    if (this.areDiceSlowing()) {
      this.stateMachine.transitionTo(DiceThrowState.SETTLING);
    }
  }

  private lastSlidingDampenTime = 0;

  private onSettlingEnter(): void {
    const ctx = this.stateMachine.getContext();
    ctx.settleStartTime = performance.now();
    this.lastSlidingDampenTime = 0;
  }

  private onSettlingUpdate(_deltaTime: number): void {
    const ctx = this.stateMachine.getContext();
    const settlingTime = performance.now() - ctx.settleStartTime;

    // After 1.5 seconds, check for sliding dice and dampen them
    // Only dampen once every 500ms to avoid over-dampening
    if (settlingTime > 1500 && settlingTime - this.lastSlidingDampenTime > 500) {
      let dampened = false;
      for (const die of this.dice) {
        if (die.isSliding()) {
          die.dampenSliding();
          dampened = true;
        }
      }
      if (dampened) {
        this.lastSlidingDampenTime = settlingTime;
      }
    }

    // Check if all dice stopped
    if (this.areDiceStopped()) {
      // Before transitioning to STOPPED, check for edge/corner landing
      // If any die is on an edge, nudge it toward nearest face (deterministic)
      let allOnValidFaces = true;
      for (const die of this.dice) {
        if (!die.isOnValidFace()) {
          die.nudgeToNearestFace();
          allOnValidFaces = false;
        }
      }

      // Only transition to STOPPED if all dice are on valid faces
      if (allOnValidFaces) {
        this.stateMachine.transitionTo(DiceThrowState.STOPPED);
      }
      // Otherwise, dice will continue settling after the nudge
    }
  }

  private onStoppedEnter(): void {
    // Dice are at rest — none are "collected" anymore (ready to be picked up again)
    this.collectedDice.clear();
    this.accumulatedEnergy = 0;
    this.lastEnergyUpdateTime = 0;
    const stoppedCtx = this.stateMachine.getContext();
    stoppedCtx.swipeDirection = { x: 0, y: 0 };
    this.resetGestureTrail();

    // Read dice values
    const values: number[] = [];
    for (const die of this.dice) {
      values.push(die.readValue());
    }

    stoppedCtx.diceResults = values;

    // Capture final pose per die (post-nudge) so spectators can correct any divergence
    const finalStates = this.dice.map((die) => ({
      position: die.getPosition(),
      quaternion: die.getRotation(),
    }));

    // Update server phase tracking and emit stopped request
    if (this.isServerAuthoritativeMode) {
      this.serverDicePhase = DicePhase.STOPPED;
      this.emit('dice:stopped_request', {
        results: values,
        finalStates,
        timestamp: Date.now(),
      });
      // Revoke authorization after completing a roll
      this.isAuthorized = false;
    }

    this.emit('dice:stopped', { values });

    // If player is already touching, auto-start pickup (no need to lift and re-press)
    // But only if still authorized (or not in authoritative mode)
    if (this.isTouching && (!this.isServerAuthoritativeMode || this.isAuthorized)) {
      this.collectedDice.clear();
      this.accumulatedEnergy = 0;
      this.holdTime = 0;
      this.lastInputPosition = { ...this.touchPosition };

      // Start holding/tracking
      this.isHolding = true;
      this.holdPosition = this.screenToWorld(
        this.touchPosition.x,
        this.touchPosition.y,
        this.config.holdHeight
      );
      this.stateMachine.forceTransitionTo(DiceThrowState.PICKING_UP);

      // Try to pick up a die if touching near one
      const groundPos = this.screenToWorld(this.touchPosition.x, this.touchPosition.y, 0.5);
      const die = this.findDiceNearPoint(groundPos);
      if (die) {
        this.pickupDie(die);
      }
    }

    this.resetGestureTrail();
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  private resetGestureTrail(initialX?: number, initialY?: number): void {
    this.gesturePoints = [];
    this.gesturePathLength = 0;
    this.gestureArcAngle = 0;
    if (typeof initialX === 'number' && typeof initialY === 'number') {
      this.gesturePoints.push({ x: initialX, y: initialY });
    }
  }

  private addGesturePoint(x: number, y: number): void {
    const MIN_SEG_LEN = 2;
    if (this.gesturePoints.length === 0) {
      this.gesturePoints.push({ x, y });
      return;
    }

    const last = this.gesturePoints[this.gesturePoints.length - 1];
    const vx = x - last.x;
    const vy = y - last.y;
    const segLen = Math.sqrt(vx * vx + vy * vy);
    if (segLen < MIN_SEG_LEN) return;

    if (this.gesturePoints.length >= 2) {
      const prev = this.gesturePoints[this.gesturePoints.length - 2];
      const pvx = last.x - prev.x;
      const pvy = last.y - prev.y;
      const prevLen = Math.sqrt(pvx * pvx + pvy * pvy);
      if (prevLen >= MIN_SEG_LEN) {
        const cross = pvx * vy - pvy * vx;
        const dot = pvx * vx + pvy * vy;
        const angle = Math.atan2(cross, dot);
        this.gestureArcAngle += angle;
      }
    }

    this.gesturePathLength += segLen;
    this.gesturePoints.push({ x, y });
  }

  /**
   * Convert screen coordinates to world position at a given height
   */
  private screenToWorld(screenX: number, screenY: number, height: number): THREE.Vector3 {
    const size = this.engine.renderer.getSize();
    sharedPointer.x = (screenX / size.width) * 2 - 1;
    sharedPointer.y = -(screenY / size.height) * 2 + 1;

    sharedRaycaster.setFromCamera(sharedPointer, this.engine.camera);

    // Use shared plane for intersection
    tempPlane.setFromNormalAndCoplanarPoint(
      tempVec3A.set(0, 1, 0),
      tempVec3A.set(0, height, 0)
    );
    const target = new THREE.Vector3();
    sharedRaycaster.ray.intersectPlane(tempPlane, target);

    return target || new THREE.Vector3(0, height, 0);
  }

  /**
   * Find a die near a world position (within pickup radius)
   */
  private findDiceNearPoint(worldPos: THREE.Vector3): Dice | null {
    for (const die of this.dice) {
      if (this.collectedDice.has(die)) continue;

      const diePos = die.getPosition();
      const dist = Math.sqrt(
        Math.pow(diePos.x - worldPos.x, 2) +
        Math.pow(diePos.z - worldPos.z, 2)
      );

      if (dist < this.config.pickupRadius) {
        return die;
      }
    }
    return null;
  }

  private pickupDie(die: Dice): void {
    die.pickup();
    this.collectedDice.add(die);
    sound.play(SoundId.DICE_PICKUP);
    this.emit('dice:pickup', { diceIndex: this.dice.indexOf(die) });
  }

  private releaseAllDice(): void {
    this.stopDragSync();
    for (const die of this.collectedDice) {
      die.release();
    }
    this.collectedDice.clear();
  }

  private startDragSync(): void {
    if (this.dragSyncInterval) return; // Already running

    this.dragSyncInterval = setInterval(() => {
      this.emitDragSync();
    }, this.DRAG_SYNC_INTERVAL);

    // Also emit immediately
    this.emitDragSync();
  }

  private stopDragSync(): void {
    if (this.dragSyncInterval) {
      clearInterval(this.dragSyncInterval);
      this.dragSyncInterval = null;
    }
  }

  private emitDragSync(): void {
    if (this.collectedDice.size === 0) return;

    // Get average position of collected dice (or first die position)
    const firstDie = this.collectedDice.values().next().value;
    if (!firstDie) return;

    const position = this.isHolding
      ? { x: this.holdPosition.x, y: this.holdPosition.y, z: this.holdPosition.z }
      : firstDie.getPosition();
    const rotation = firstDie.getRotation();
    const timestamp = Date.now();
    this.dragSeq += 1;

    this.emit('dice:drag_sync', {
      position,
      rotation,
      seq: this.dragSeq,
      timestamp,
    });
  }

  private updateHeldDicePositions(deltaTime: number): void {
    if (!this.isHolding) return;

    const spacing = 1.2;
    const count = this.collectedDice.size;
    let index = 0;

    for (const die of this.collectedDice) {
      // Calculate target position - offset each die slightly from hold position
      const offsetX = (index - (count - 1) / 2) * spacing;
      const targetPos = new THREE.Vector3(
        this.holdPosition.x + offsetX,
        this.config.holdHeight,
        this.holdPosition.z
      );

      // Smooth lerp to follow finger (magnetic attachment)
      const currentPos = die.getPosition();
      const lerpedPos = new THREE.Vector3(
        currentPos.x + (targetPos.x - currentPos.x) * this.MAGNET_SPEED * deltaTime,
        currentPos.y + (targetPos.y - currentPos.y) * this.MAGNET_SPEED * deltaTime,
        currentPos.z + (targetPos.z - currentPos.z) * this.MAGNET_SPEED * deltaTime
      );

      die.setPosition({
        x: lerpedPos.x,
        y: lerpedPos.y,
        z: lerpedPos.z,
      });

      // Continuous rotation while held (like prototype)
      // Each die rotates at slightly different rates for variety
      die.addRotation({
        x: deltaTime * 2 * (1 + index * 0.1),
        y: 0,
        z: deltaTime * 1.5 * (1 + index * 0.1),
      });

      index++;
    }
  }

  private allDiceCollected(): boolean {
    return this.collectedDice.size === this.dice.length;
  }

  private areDiceSlowing(): boolean {
    const threshold = 2; // Speed threshold
    for (const die of this.dice) {
      if (die.getSpeed() > threshold) {
        return false;
      }
    }
    return true;
  }

  private areDiceStopped(): boolean {
    for (const die of this.dice) {
      if (!die.isStopped()) {
        return false;
      }
    }
    return true;
  }

  /**
   * Get current state
   */
  get state(): DiceThrowState | null {
    return this.stateMachine.current;
  }

  /**
   * Get current energy
   */
  get energy(): number {
    return this.accumulatedEnergy;
  }

  /**
   * Get dice count collected
   */
  get collected(): number {
    return this.collectedDice.size;
  }

  /**
   * Get dice results (after stopped)
   */
  get results(): number[] {
    return this.stateMachine.getContext().diceResults;
  }

  /**
   * Enable or disable dice input
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    // Labels managed by updatePickupLabels() each frame — no manual show/hide needed
  }

  /**
   * Check if dice input is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get dice array (for camera tracking)
   */
  getDice(): Dice[] {
    return this.dice;
  }

  /**
   * Reset to ready state
   */
  reset(positions: Array<{ x: number; y: number; z: number }>): void {
    this.collectedDice.clear();
    this.accumulatedEnergy = 0;
    this.holdTime = 0;

    for (let i = 0; i < this.dice.length; i++) {
      this.dice[i].reset(positions[i] ?? { x: 0, y: 1, z: 0 });
    }

    this.stateMachine.forceTransitionTo(DiceThrowState.READY);
  }

  // ============================================================================
  // SERVER-AUTHORITATIVE METHODS
  // ============================================================================

  /**
   * Enable server-authoritative mode
   * When enabled, local actions are gated by server authorization
   */
  getServerAuthoritativeMode(): boolean {
    return this.isServerAuthoritativeMode;
  }

  setServerAuthoritativeMode(enabled: boolean): void {
    this.isServerAuthoritativeMode = enabled;
    if (enabled) {
      this.isAuthorized = false;
    }
  }

  /**
   * Defer physics on throw (for HI_LO determinism).
   * When true, onThrowingEnter captures throw state and sends to server
   * but freezes dice until applyServerThrow() arrives.
   * This ensures all clients start physics from the same server broadcast.
   */
  setDeferPhysicsOnThrow(defer: boolean): void {
    this.deferPhysicsOnThrow = defer;
  }

  /** Whether local physics is deferred to the server echo (HI_LO mode). */
  isPhysicsDeferred(): boolean {
    return this.deferPhysicsOnThrow;
  }

  /**
   * Pickup all dice immediately (disable physics).
   * Used during HI_LO to prevent dice from having active physics
   * between creation and player interaction.
   */
  pickupAllDice(): void {
    for (const die of this.dice) {
      die.pickup();
    }
  }

  /**
   * Server authorized us to roll
   */
  handleRollAuthorize(): void {
    this.isAuthorized = true;
    this.serverDicePhase = DicePhase.IDLE;
  }

  /**
   * Handle dice phase change from server
   * This is called for remote players' dice phases
   */
  handleDicePhase(playerId: string, phase: DicePhase, throwParams?: DiceThrowParams): void {

    // This is for remote players - emit event for GhostDiceManager to handle
    this.emit('dice:remote_phase', { playerId, phase, throwParams });

    // If it's a throw with params, we need to apply the physics on ghost dice
    if (phase === DicePhase.THROWN && throwParams) {
      this.emit('dice:remote_throw', { playerId, throwParams });
    }
  }

  /**
   * Apply throw physics from server (for our own throw, echoed back)
   * This ensures deterministic simulation across all clients by applying
   * the exact same initial physics state on all clients.
   */
  applyServerThrow(throwParams: DiceThrowParams): void {
    for (let i = 0; i < throwParams.diceStates.length && i < this.dice.length; i++) {
      const state = throwParams.diceStates[i];
      const die = this.dice[i];

      // First, fully reset the body to clear any accumulated state
      // This is critical for deterministic simulation
      die.body.velocity.setZero();
      die.body.angularVelocity.setZero();
      die.body.force.setZero();
      die.body.torque.setZero();

      // Set exact position on both body and mesh
      die.body.position.set(state.position.x, state.position.y, state.position.z);
      die.mesh.position.set(state.position.x, state.position.y, state.position.z);

      // Set exact rotation on both body and mesh
      die.body.quaternion.set(state.rotation.x, state.rotation.y, state.rotation.z, state.rotation.w);
      die.mesh.quaternion.set(state.rotation.x, state.rotation.y, state.rotation.z, state.rotation.w);

      // Set exact velocity
      die.body.velocity.set(state.velocity.x, state.velocity.y, state.velocity.z);

      // Set exact angular velocity
      die.body.angularVelocity.set(state.angularVelocity.x, state.angularVelocity.y, state.angularVelocity.z);

      // Now release - enables physics (mass=1, wakes body)
      die.release();
    }

    // Clear held state
    this.collectedDice.clear();
    this.isHolding = false;

    // Transition to rolling state
    if (this.stateMachine.current !== DiceThrowState.ROLLING) {
      this.stateMachine.forceTransitionTo(DiceThrowState.ROLLING);
    }
  }

  /**
   * Get server-authoritative phase
   */
  getServerPhase(): DicePhase {
    return this.serverDicePhase;
  }

  /**
   * Check if authorized to roll
   */
  isRollAuthorized(): boolean {
    return this.isAuthorized;
  }

  /**
   * Revoke roll authorization
   */
  revokeAuthorization(): void {
    this.isAuthorized = false;
  }

  // ============================================================================
  // CONFIG METHODS (for editor live tweaking)
  // ============================================================================

  /**
   * Update config at runtime (for editor live tweaking)
   */
  updateConfig(updates: Partial<DiceControllerConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current config
   */
  getConfig(): Required<DiceControllerConfig> {
    return { ...this.config };
  }

  /**
   * Dispose
   */
  dispose(): void {
    this.stopDragSync();
    for (const die of this.dice) {
      die.dispose();
    }
    this.dice = [];
    this.removeAllListeners();
  }
}
